import React from 'react';
import styles from './Styles/EventOps.module.css';
import { reviewReasons, REASON_LABEL } from './viewerKeys';

// One matched-athlete chip in the photo viewer (SCRUM-338). Style + actions
// follow the match's status: auto visible (blue, Suppress), held for review
// (light blue, Confirm/Suppress), confirmed (purple; dashed when inferred;
// Suppress), suppressed (red, Restore). Colours match the photo count cards.
// The ring is the same one the photo tile gets while this athlete is
// filtered (SCRUM-408); purchased / in cart is a pill, as on the tile.
// `readOnly` hides the actions (deleted photos keep their chips for context only).
// The 🔍 (top-right, clear of the $ / cart badge) opens the athlete's reference
// selfies in the rail, like the one on the "Add a match" cards; the selfie
// itself is clickable too.
// `threshold` is the event's matchThreshold (SCRUM-357) — passed through to
// reviewReasons() to derive the "low score" reason chip.
export default function MatchChip({ match, onConfirm, onSuppress, onRestore, selfieUrl, readOnly = false, onShowProfile, threshold }) {
  const held = match.status === 'auto' && !match.visible;
  const cls = [
    styles.faceCard,
    match.status === 'suppressed' ? styles.faceSuppressed
      : held ? styles.faceReview
      : match.status === 'confirmed' ? styles.faceMatchedManual
      : styles.faceMatchedAuto,
    match.inferred ? styles.faceMatchedInferred : '',
  ].join(' ');
  const label = match.inferred ? 'inferred' : match.manual ? 'manual'
    : match.status === 'confirmed' ? `confirmed ${match.confidenceScore}%`
    : `${held ? 'review' : 'face rec'} ${match.confidenceScore}%`;
  const hint = match.inferred
    ? 'Inferred from the shooting sequence: this frame sits between two recognised frames of this athlete on the same camera'
    : held
      ? 'Held for review — parents cannot see it until you confirm'
      : undefined;
  const reasons = held ? reviewReasons(match, threshold) : [];
  return (
    <div className={cls} title={`${match.fullName} — ${label}${match.status === 'suppressed' ? ' (suppressed)' : ''}${match.commerce === 'purchased' ? ' · purchased' : match.commerce === 'cart' ? ' · in cart' : ''}`}>
      {onShowProfile && (
        <button
          type="button"
          className={`${styles.facePeek} ${styles.facePeekRight}`}
          title="Open this athlete's reference selfies to compare"
          aria-label={`Show ${match.fullName} profile`}
          onClick={(e) => { e.stopPropagation(); onShowProfile(); }}
        >
          🔍
        </button>
      )}
      {match.commerce === 'purchased' && <span className={styles.faceSoldBadge}>$</span>}
      {match.commerce === 'cart' && <span className={styles.faceCartBadge}>🛒</span>}
      {onShowProfile ? (
        <button type="button" className={styles.faceImgBtn} onClick={onShowProfile} title="Open this athlete's reference selfies to compare" aria-label={`Show ${match.fullName} profile`}>
          {selfieUrl ? <img src={selfieUrl} alt={match.fullName} className={styles.faceImg} /> : <div className={styles.faceImgEmpty}>?</div>}
        </button>
      ) : (
        selfieUrl ? <img src={selfieUrl} alt={match.fullName} className={styles.faceImg} /> : <div className={styles.faceImgEmpty}>?</div>
      )}
      <span className={styles.faceName}>{(match.fullName || '').trim()}</span>
      {reasons.length > 0 && (
        <span className={styles.reasonChips}>
          {reasons.map((k) => <span className={styles.reasonChip} key={k}>{REASON_LABEL[k] ?? k}</span>)}
        </span>
      )}
      <span
        className={match.status === 'suppressed' ? styles.faceSubSuppressed : held ? styles.faceSubReview : match.status === 'confirmed' ? styles.faceSubManual : styles.faceSubAuto}
        title={hint}
      >
        {label}
      </span>
      {!readOnly && <span className={styles.faceActions}>
        {match.status === 'suppressed' ? (
          <button type="button" className={styles.faceAction} aria-label="Restore" title="Restore" onClick={() => onRestore(match)}>↺ Restore</button>
        ) : (
          <>
            {held && <button type="button" className={styles.faceAction} aria-label="Confirm" title="Confirm this match" onClick={() => onConfirm(match)}>✓</button>}
            {match.manual ? (
              <button type="button" className={`${styles.faceAction} ${styles.faceActionDanger}`} aria-label="Remove" title="Remove this manual tag" onClick={() => onSuppress(match)}>✕ Remove</button>
            ) : (
              <button type="button" className={`${styles.faceAction} ${styles.faceActionDanger}`} aria-label="Suppress" title="Suppress — hidden from the parent and never re-matched" onClick={() => onSuppress(match)}>✕</button>
            )}
          </>
        )}
      </span>}
    </div>
  );
}
